import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

export interface SpeakerLineTimelineItem {
	_type: 'SpeakerLineTimelineItem';
	timestamp: string;
	speaker: string;
	line: string;
	startTimeOffset: number;
	endTimeOffset: number;
}

export interface AIChatMessageTimelineItem {
	_type: 'AIChatMessageTimelineItem';
	timestamp: string;
	id: string;
	role: Api.AIChatRole;
	text: string;
}

export type CoachingTimelineItem = SpeakerLineTimelineItem | AIChatMessageTimelineItem;

interface QueryParams {
	phoneCallId: string | undefined;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}

export interface CallAdherenceTimelineResult {
	data: CoachingTimelineItem[] | undefined;
	isLoading: boolean;
	error: unknown;
}

const BASE_QUERY_KEY = '/phoneCall/<id>/coachingTimeline';

export function useCallAdherenceTimelineQuery({
	phoneCallId,
	enabled = true,
	impersonationContext,
}: QueryParams): CallAdherenceTimelineResult {
	const userSession = useUserSession();
	const queryEnabled = Boolean(enabled && phoneCallId);

	const impersonationKey = impersonationContext
		? { accountId: impersonationContext.account?.id, userId: impersonationContext.user?.id }
		: undefined;

	const query = useQuery({
		queryKey: [BASE_QUERY_KEY, phoneCallId, impersonationKey],
		queryFn: () =>
			userSession.webServiceHelper.callAsync<CoachingTimelineItem[]>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `phoneCall/${phoneCallId}/coachingTimeline`,
					impersonationContext,
				}),
				'GET'
			),
		enabled: queryEnabled,
	});

	return {
		data: queryEnabled ? query.data : undefined,
		isLoading: queryEnabled && query.isLoading,
		error: queryEnabled ? query.error : null,
	};
}
